/**
 * @ngdoc service
 * @name app.categoryService
 * @description
 * # Categories Service
 * Service to talk with backend api to get categories information.
 * @requires app.apiService
 * @requires app.weatherService
 * @requires app.livefeedService
 * @requires app.localService
 * @requires constants:config
 * @requires constants:states
 * @requires constants:storageKeys
 * @requires ng:$q
**/
/*---------------------------------------------------------*\
 * Imports
\*---------------------------------------------------------*/
import angular from 'angular';
import _ from 'lodash';
/*---------------------------------------------------------*\
 * categoryService
\*---------------------------------------------------------*/
/* @ngInject */
function categoryService(apiService, config, states, weatherService, $q, livefeedService, localService, storageKeys) {
  const tw = {
    nj: {
      id: 't&w_nj',
      title: 'NJ CH 61 T&W',
      type: 'component',
      state: {
        name: states.enlargedLiveFeed,
        params: {
          feedType: 'T&W',
          region: 'NJ'
        }
      }
    },
    wc: {
      id: 't&w_wc',
      title: 'WC CH 61 T&W',
      type: 'component',
      state: {
        name: states.enlargedLiveFeed,
        params: {
          feedType: 'T&W',
          region: 'WC'
        }
      }
    },
    ct: {
      id: 't&w_ct',
      title: 'CT CH 61 T&W',
      type: 'component',
      state: {
        name: states.enlargedLiveFeed,
        params: {
          feedType: 'T&W',
          region: 'CT'
        }
      }
    },
    nyc: {
      id: 't&w_nyc',
      title: 'NYC CH 61 T&W',
      type: 'component',
      state: {
        name: states.enlargedLiveFeed,
        params: {
          feedType: 'T&W',
          region: 'NYC'
        }
      }
    },
    li: {
      id: 't&w_li',
      title: 'LI CH 61 T&W',
      type: 'component',
      state: {
        name: states.enlargedLiveFeed,
        params: {
          feedType: 'T&W',
          region: 'LI'
        }
      }
    }
  };
  const menu = [
    { id: 'news', title: 'News', state: states.home },
    { id: 'io_extra', title: 'Extra', state: states.io_extra },
    { id: 'photos', title: 'Photos', state: states.photos },
    { id: 'in_depth', title: 'In Depth', state: states.in_depth },
    { id: 'features', title: 'Features', state: states.features },
    { id: 'weather', title: 'Weather' },
    { id: 'traffic', title: 'Traffic' }
  ];
  const staticSections = {
    news: [
      // {
      //   id: 'poll',
      //   title: 'News 12 Poll',
      //   type: 'component'
      // },
      {
        id: 'archives',
        title: 'Archives',
        type: 'component'
      },
      {
        id: 'other_regions',
        title: 'Other Regions',
        type: 'component'
      }
    ],
    weather: [
      {
        id: 'weather_center',
        title: 'Weather Center',
        type: 'component',
        state: {
          name: states.weather_center
        }
      },
      tw.nj,
      tw.wc,
      tw.ct,
      tw.nyc,
      tw.li,
      {
        id: 'forecast',
        title: 'Forecast Video',
        type: 'component',
        state: {
          name: states.videoplayer
        }
      }
    ],
    traffic: [
      {
        id: 'traffic_center',
        title: 'Traffic Center',
        type: 'component',
        state: {
          name: states.traffic_center
        }
      },
      tw.nj,
      tw.wc,
      tw.ct,
      tw.nyc,
      tw.li
    ]
  };
  const allRegions = localService.get(storageKeys.regions);
  // APIs
  /**
   * @ngdoc
   * @name app.categoryService#getCategories
   * @methodOf app.categoryService
   * @param {string} regionId selected region abbreviation, case 'Bronx' => 'BX',
   * 'New Jersey' => 'NJ', max of two characters..
   * @example
   * ``` js
   * categoryService.getCategories('LI');
   * ```
   * @description
   * calls {@link app.apiService#wrap apiService.wrap()} method with current base url,
   * '/news/categories/' and regionId'.
   * @returns {httpPromise} Promise that will resolve into an array or categories for
   * current region.
   **/
  this.getCategories = (regionId) => {
    return apiService.wrap(config.relBaseUrlAPi + '/news/categories/' + regionId,
      {}, 'categories')
      .then((res) => {
        if (!res.data) {
          return [];
        }
        return this.buildMenu(res.data, regionId).then((menu) => {
          return menu;
        });
      });
  };

  /**
   * @ngdoc
   * @name app.categoryService#buildMenu
   * @methodOf app.categoryService
   * @param {array} data array of all available categories.
   * @param {string} region selected region abbreviation, case 'Bronx' => 'BX',
   * 'New Jersey' => 'NJ', max of two characters.
   * @example
   * ``` js
   * let categories = [
   *   {
   *     hasStories:true,
   *     id:'news',
   *     sections: [],
   *     state:'app.home.news',
   *     title:"News"
   *   },
   *   {
   *     hasStories:false,
   *     id:'extra',
   *     sections: [],
   *     state:'app.home.extra',
   *     title:"Extra"
   *   }
   * ]
   * categoryService.buildMenu(categories,'LI');
   * ```
   * @description
   * It takes {@link app.categoryService#menu} and Builds menu items accordingly.
   * calls {@link app.apiService#wrap apiService.wrap()} method with current base url,
   * '/news/categories/' and regionId'.
   * @returns {httpPromise} Promise that will resolve into an array or categories for current region.
   **/
  this.buildMenu = (data, region) => {
    const promises = [];
    const defer = $q.defer();
    menu.forEach((category) => {
      promises.push(this.appendSections(category, data[category.id] || {}, region));
    });

    $q.all(promises).then(() => {
      defer.resolve(menu);
    });
    return defer.promise;
  };

  /**
   * @ngdoc
   * @name app.categoryService#appendSections
   * @methodOf app.categoryService
   * @param {object} category array of all available categories.
   * @param {array} stories Stories object to form the navbar link items.
   * @param {region} region selected region abbreviation, case 'Bronx' => 'BX',
   * 'New Jersey' => 'NJ', max of two characters.
   * @example
   * ``` js
   * let stories = {
   *   crime:"Crime",
   *   local: "Top &apos;Stories&apos;",
   *   tsnw: "World News"
   * }
   * let category = {
   *   hasStories:true,
   *   id:'news',
   *   sections: [],
   *   state:'app.home.news',
   *   title:"News"
   * }
   * categoryService.appendSections(category, stories,'LI');
   * ```
   * @description
   * It takes {@link app.categoryService#menu} and Builds menu items accordingly.
   * calls {@link app.apiService#wrap apiService.wrap()} method with current base url,
   * '/news/categories/' and regionId'.
   * It is called inside of {@link app.categoryService#buildMenu} to get url information for the navbar
   * @returns {httpPromise} Promise that will resolve into an array or categories for current region.
   **/
  this.appendSections = (category, stories, region) => {
    const defer = $q.defer();
    const promises = [];
    stories = Object.keys(stories).map((k) => {
      return {
        id: k,
        title: stories[k],
        type: 'storylist'
      };
    });
    category.hasStories = stories.length > 0;
    category.sections = stories.concat(staticSections[category.id] || []);
    category.sections.forEach((section) => {
      const innerDefer = $q.defer();
      if (section.id === 'forecast') {
        weatherService.getWeatherForecast(region).then((data) => {
          const currentItem = {};
          if (data && data.rss && data.rss.channel) {
            currentItem.videoUrl = data.rss.channel.item['media:content'].url;
          } else {
            // TODO setup a default video error image instead
            currentItem.videoUrl = '';
          }
          section.state.params = { currentStory: { story: currentItem } };
          innerDefer.resolve();
        }).catch(() => {
          // if forecast video fails then we shouldnt stop the whole menu from building. So make video empty.
          // TODO setup a default video error image instead
          const currentItem = {};
          currentItem.videoUrl = '';
          section.state.params = { currentStory: { story: currentItem } };
          innerDefer.resolve();
        });
      } else if (_.contains(section.id, 't&w')) {
        // TODO, check this filter, NYC forecast is not being displayed.
        const currentRegion = _.filter(allRegions, (region) => {
          let ID = section.id.substring(section.id.indexOf('_') + 1, section.id.length).toUpperCase();
          ID = ID === 'NYC' ? 'BX' : ID;
          return region.abbr === ID;
        });
        const currentItem = {
          story: {
            videoUrl: _.isUndefined(currentRegion[0]) ?
              currentRegion[0] :
              livefeedService.getTWLivefeed(currentRegion[0].twCallSign)
          }
        };
        section.state.params = { currentStory: currentItem };
        innerDefer.resolve();
      } else {
        innerDefer.resolve();
      }
      promises.push(innerDefer.promise);
    });

    $q.all(promises).then(() => {
      defer.resolve();
    });

    return defer.promise;
  };
}

categoryService.$inject = [
  'apiService', 'config', 'states', 'weatherService', '$q', 'livefeedService', 'localService', 'storageKeys'
];

const api = angular
  .module('categoryService', ['config', 'apiService', 'constants'])
  .service('categoryService', categoryService);

export default api;



/** WEBPACK FOOTER **
 ** ./client/app/services/apis/categories.js
 **/